import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import PageContainer from "../../components/common/PageContainer";
import BaseTable from "../../components/table/BaseTable";
import Card from "../../components/ui/Card";
import SectionCard from "../../components/ui/SectionCard";
import AgentHeader from "../../components/agents/AgentHeader";
import Loader from "../../components/ui/Loader/Loader";

import {
  Activity,
  ChartGantt,
  Clock,
  MessageSquare,
  Mic,
  Phone,
  User,
  ChartNoAxesCombined as ChartNoAxesCombinedIcon,
} from "lucide-react";

import AgentUseageChart from "../../components/agents/useage/AgentUseageChart";
import MetaConversationCounts from "../../components/agents/MetaConversationCounts";
import { limitsFromSources } from "../../utils/conversationCounts";
import { useAgentBillingDetailsQuery } from "../../queries/agents/useAgentsQuery";
import { useAuthStore } from "../../store/authStore";
import { formatDateDDMMYYYY } from "../../hooks/commonHooks";
import { formatVoiceClock } from "../../utils/voiceClock";
import { SHOW_VOICE_AND_META } from "../../config/features";

import "../../styles/VoiceUsage.css";

function formatInr(amount) {
  const value = Number(amount);
  if (Number.isNaN(value)) return "—";
  return `₹${value.toLocaleString("en-IN")}`;
}

function formatCount(value) {
  const number = Number(value);
  if (Number.isNaN(number)) return "—";
  return number.toLocaleString("en-IN");
}

function queryErrorMessage(error) {
  if (!error) return "Failed to load billing details";
  if (typeof error === "string") return error;
  return error.message || "Failed to load billing details";
}

function formatBillingCycle(cycle) {
  if (!cycle?.start || !cycle?.end) return "—";
  return `${formatDateDDMMYYYY(cycle.start)} – ${formatDateDDMMYYYY(cycle.end)}`;
}

const GROUP_BY_OPTIONS = [
  { label: "Day", value: "day" },
  { label: "Week", value: "week" },
  { label: "Month", value: "month" },
];

const ACTIVITY_COLUMNS = [
  { title: "Action", dataIndex: "label", key: "label" },
  {
    title: "Leads",
    dataIndex: "leads",
    key: "leads",
    className: "activity-col-count activity-breakdown-leads",
    render: (value) => formatCount(value),
  },
  {
    title: "Times",
    dataIndex: "events",
    key: "events",
    className: "activity-col-count",
    render: (value) => formatCount(value),
  },
];

export default function AgentUsagePage() {
  const navigate = useNavigate();
  const { agentId: routeAgentId } = useParams();
  const { role, user } = useAuthStore();
  const isAgent = role === "agent";
  const agentId = routeAgentId || (isAgent ? user?.id : null);

  const [groupBy, setGroupBy] = useState("day");
  const { data, isLoading, isError, error } = useAgentBillingDetailsQuery(agentId, groupBy);

  if (!agentId) {
    return (
      <PageContainer>
        <p className="text-muted m-0">Open an agent from the usage list to see billing details.</p>
      </PageContainer>
    );
  }

  if (isLoading) return <Loader />;

  if (isError || !data) {
    return (
      <PageContainer>
        <div className="agent-usage-header">
          <AgentHeader navigate={navigate} />
        </div>
        <p className="text-muted m-0">{queryErrorMessage(error)}</p>
      </PageContainer>
    );
  }

  const plan = data.plan ?? {};
  const minutes = data.minutes ?? {};
  const billing = data.billing ?? {};
  const sources = data.sources ?? {};
  const callSource = sources.call ?? {};
  const chatSource = sources.chat ?? {};
  const activitySource = sources.activity ?? {};
  const activityActions = Array.isArray(activitySource.actions) ? activitySource.actions : [];
  const usagePercent = Number(minutes.usage_percent) || 0;
  const progressWidth = Math.min(Math.max(usagePercent, 0), 100);

  return (
    <PageContainer>
      <div className="agent-usage-page">
        <div className="agent-usage-header">
          <AgentHeader navigate={navigate} />
        </div>

        <SectionCard title="Agent" icon={User}>
          <Card className="max-w-0 m-0">
            <h2 className="agent-title m-0">{data.agent_name}</h2>
          </Card>
        </SectionCard>

        <header>
          <div className="d-flex justify-between align-center mb-1">
            <div>
              <span className="d-flex align-center gap-2">
                <Mic size={33} />
                <h1 className="text-lg fw-semibold text-primary">Voice Usage</h1>
              </span>
              <p className="billing-cycle-badge">
                <span className="billing-label">Billing Cycle</span>
                <span className="billing-value">{formatBillingCycle(data.billing_cycle)}</span>
              </p>
            </div>
            <div className="voice-status">
              <span className="voice-status-dot" />
              {data.status}
            </div>
          </div>
        </header>

        <section className="voice-usage-section">
          <div className="agent-usage-grid">
            <SectionCard title="Plan Details" icon={ChartGantt} className="mb-2">
              <Card className="max-w-0">
                <div className="d-flex justify-between py-1 border-bottom">
                  <span className="text-muted">Plan</span>
                  <span className="voice-badge">{plan.name}</span>
                </div>
                <div className="d-flex justify-between py-1 border-bottom">
                  <span className="text-muted">Included minutes</span>
                  <span className="fw-semibold">{formatVoiceClock(plan.included_minutes)}</span>
                </div>
                <div className="d-flex justify-between py-1 border-bottom">
                  <span className="text-muted">Overage rate</span>
                  <span className="fw-semibold">₹{formatCount(plan.overage_rate_inr)} / min</span>
                </div>
                <div className="d-flex justify-between py-1">
                  <span className="text-muted">Monthly cap</span>
                  <span className="fw-semibold">{formatInr(plan.monthly_cap_inr)}</span>
                </div>
              </Card>
            </SectionCard>

            <SectionCard title="Minutes Usage" icon={Clock} className="mb-2">
              <Card className="max-w-0">
                <div className="voice-metrics-grid">
                  <div className="voice-metric-box">
                    <div className="text-sm text-muted">Used</div>
                    <div className="text-lg fw-semibold text-primary">
                      {formatVoiceClock(minutes.used)}
                    </div>
                    <div className="text-xs text-muted">
                      of {formatVoiceClock(minutes.included)} included
                    </div>
                  </div>
                  <div className="voice-metric-box">
                    <div className="text-sm text-muted">Remaining</div>
                    <div className="text-lg fw-semibold text-primary">
                      {formatVoiceClock(minutes.remaining)}
                    </div>
                    <div className="text-xs text-muted">until overage kicks in</div>
                  </div>
                  <div className="voice-metric-box">
                    <div className="text-sm text-muted">Extra Minutes</div>
                    <div className="text-lg fw-semibold text-primary">
                      {formatVoiceClock(minutes.extra)}
                    </div>
                    <div className="text-xs text-muted">{formatVoiceClock(minutes.extra)} extra</div>
                  </div>
                </div>
                <div className="voice-progress-section">
                  <div className="d-flex justify-between text-sm mb-1">
                    <span className="text-muted">Usage progress</span>
                    <span className="text-accent fw-semibold">{usagePercent}%</span>
                  </div>
                  <div className="voice-progress-track">
                    <div className="voice-progress-fill" style={{ width: `${progressWidth}%` }} />
                  </div>
                </div>
              </Card>
            </SectionCard>
          </div>

          <div className="agent-usage-sources">
            <SectionCard title="Call" icon={Phone}>
              <Card className="max-w-0">
                <div className="voice-metric-box">
                  <div className="text-lg fw-semibold text-primary">
                    {formatCount(callSource.minutes)}
                    <span className="text-sm text-muted ml-0-5">min</span>
                  </div>
                  <div className="text-xs text-muted">Billed</div>
                  <div className="text-sm text-muted">{formatCount(callSource.leads)} leads</div>
                </div>
              </Card>
            </SectionCard>

            <SectionCard title="Chat" icon={MessageSquare}>
              <Card className="max-w-0">
                <div className="voice-metric-box">
                  <div className="text-lg fw-semibold text-primary">
                    {formatCount(chatSource.minutes)}
                    <span className="text-sm text-muted ml-0-5">min</span>
                  </div>
                  <div className="text-xs text-muted">Not billed</div>
                  <div className="text-sm text-muted">{formatCount(chatSource.leads)} leads</div>
                </div>
              </Card>
            </SectionCard>

            <SectionCard title="Activity" icon={Activity}>
              <Card className="max-w-0">
                <div className="voice-metric-box">
                  <div className="text-lg fw-semibold text-primary">
                    {formatCount(activitySource.leads)}
                    <span className="text-sm text-muted ml-0-5">leads</span>
                  </div>
                  <div className="text-xs text-muted">{formatCount(activitySource.events)} actions</div>
                </div>
              </Card>
            </SectionCard>
          </div>

          {SHOW_VOICE_AND_META && (
            <MetaConversationCounts
              counts={data.conversation_counts}
              limits={limitsFromSources({
                whatsapp: data.whatsapp,
                properties: Array.isArray(data.properties) ? data.properties : [],
              })}
            />
          )}

          <div className="agent-usage-chart-section">
            <SectionCard
              title="Usage Chart"
              icon={ChartNoAxesCombinedIcon}
              className="usage-chart-card"
              actions={
                <div className="usage-group-toggle" role="group" aria-label="Chart grouping">
                  {GROUP_BY_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`usage-group-btn${groupBy === option.value ? " is-active" : ""}`}
                      aria-pressed={groupBy === option.value}
                      onClick={() => setGroupBy(option.value)}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              }
            >
              <AgentUseageChart data={data.chart ?? []} groupBy={groupBy} />
            </SectionCard>
          </div>

          <div className="agent-usage-insights">
          {activityActions.length > 0 && (
            <SectionCard title="Activity breakdown" icon={Activity} className="usage-insight-card">
              <BaseTable columns={ACTIVITY_COLUMNS} data={activityActions} rowKey="action" />
            </SectionCard>
          )}

          <SectionCard
            title="Billing Estimate"
            className="d-flex flex-column voice-billing-section-card usage-insight-card"
          >
              <Card className="max-w-0 flex-grow-1">
                <div className="voice-billing-grid">
                  <div className="voice-metric-box voice-highlight">
                    <div className="text-sm text-muted">Estimated Total Bill</div>
                    <div className="text-xl fw-semibold text-accent">
                      {formatInr(billing.estimated_total_inr)}
                    </div>
                    <div className="text-xs text-muted">Based on current usage</div>
                  </div>
                  <div className="voice-metric-box">
                    <div className="text-sm text-muted">Estimated Overage</div>
                    <div className="text-xl fw-semibold text-primary">
                      {formatInr(billing.estimated_overage_inr)}
                    </div>
                    <div className="text-xs text-muted">
                      {formatVoiceClock(minutes.extra)} extra
                    </div>
                  </div>
                  <div className="voice-metric-box">
                    <div className="text-sm text-muted">Monthly Cap</div>
                    <div className="text-xl fw-semibold text-primary">
                      {formatInr(billing.monthly_cap_inr)}
                    </div>
                    <div className="text-xs text-muted">
                      {formatInr(billing.headroom_inr)} headroom left
                    </div>
                  </div>
                  <div className="voice-metric-box">
                    <div className="text-sm text-muted">Overage Rate</div>
                    <div className="text-xl fw-semibold text-primary">
                      ₹{formatCount(billing.overage_rate_inr)}/min
                    </div>
                    <div className="text-xs text-muted">
                      Applies after {formatVoiceClock(plan.included_minutes)}
                    </div>
                  </div>
                </div>
              </Card>
            </SectionCard>
          </div>
        </section>
      </div>
    </PageContainer>
  );
}
